import type { FeatureFlagKey } from '@parkify/api-types'
import { isKillSwitchAdmin } from './killSwitchAdmins'
import type { Permission } from './permissions'

// Which console screens are Parkify-internal, in one place.
//
// This used to live inside App.tsx, where only the sidebar and the direct-URL
// guard could reach it. But a screen is not the only way its data reaches the
// page: the Home attention strip, the global search bar, and Avery's
// notification CTAs all read the same endpoints independently. Gating just the
// nav item left those surfaces showing the data anyway — a fake gate.
//
// Everything that surfaces a staff-only screen's data must consult
// `isStaffOnlyScreen` so the rule can't be half-applied. Home.tsx and
// AveryWidget.tsx import it directly rather than going through App.tsx, which
// would be a circular import.

export type ScreenId =
  | 'home' | 'avery' | 'support' | 'transactions' | 'zones' | 'qr'
  | 'permits' | 'validations' | 'tenants' | 'accounting' | 'analytics' | 'customers'
  | 'subscriptions'
  | 'integrations' | 'parkify_admin' | 'client_admin' | 'enforcement'
  | 'disputes' | 'kpi_revenue' | 'kill_switches' | 'refund_requests' | 'intake_submissions'

/**
 * Parkify-internal screens. They expose secret names, vendor identities, and
 * infra details, so they must never render for a client-scoped operator — even
 * one with an `owner` role on their own org. Layered on top of the
 * (tenant-blind) role-permission check (O9/D3).
 *
 * `customers` — Mitte decoupling (2026-06-10): the plate leaderboard / LTV /
 * enrollment list is sourced entirely from the Mitte mirror
 * (parkify-cs /admin/customers*), which is now support-only. The native pay-api
 * has no operator-facing customers source yet, and net-new native operators have
 * no Mitte history to show, so the screen stays a Parkify-staff tool rather than
 * rendering stale/empty data to operators.
 *
 * NOTE: `subscriptions` is deliberately NOT in this set. It used to be reachable
 * only as a tab inside `customers`, so the staff-only rule above swallowed it —
 * every client-scoped operator was blocked from their OWN subscribers, which no
 * one intended: `view.subscribers` is granted to manager/operator/viewer, the
 * screen carries a per-tenant `subscribers` feature flag, and it reads the
 * native, scope-filtered /api/subscriptions rather than the Mitte mirror. It is
 * its own top-level screen now precisely so a staff-only decision about Mitte
 * data can't silently take operator-owned data with it again.
 *
 * `support` — triages help@parkify.com email, Parkify's own support function,
 * not something a client-scoped operator runs (#585 flagged it internal-only).
 *
 * `qr` — owner decision, 2026-10-13: standalone bulk printing and artwork
 * management belong to Parkify. Clients see each zone's QR under Locations.
 *
 * `disputes` — held to Parkify staff by request (2026-08-03), until further
 * notice. Chargeback handling is Parkify-side for now; an operator seeing a
 * dispute they can't act on generates a support ticket, not a resolution. This
 * one is a POLICY hold rather than a data-sensitivity call, so it is the most
 * likely entry here to be reverted — removing it from this set restores every
 * surface at once.
 *
 * `refund_requests` — parkify-cs's `refund_requests` table (Avery/account-
 * portal-filed refund REQUESTS awaiting a human decision) carries no
 * clientId/tenant column at all, unlike Transactions or Accounting. A DATA
 * fact, not a policy choice: there is nothing to scope a client-scoped
 * operator's view down TO, so the only safe options are "every tenant's
 * requests" or "none" — this is a data-sensitivity hold like `customers`, not
 * a policy hold like `disputes`. The pay-side proxy routes
 * (support/refund-request-queue.ts) enforce the same restriction
 * server-side (mirroring handleSupportRefund's clientId!=null 403), so this
 * is defence in depth, not the only gate.
 */
export const PARKIFY_STAFF_ONLY = new Set<ScreenId>([
  'integrations', 'parkify_admin', 'customers', 'support', 'kpi_revenue',
  'kill_switches', 'disputes', 'refund_requests', 'qr', 'intake_submissions',
])

/**
 * The route AveryWidget.tsx checks against `useLocation().pathname` to decide
 * whether to send `screen: 'parkify_admin'` on a chat turn — the string the
 * Worker's ADMIN_SCREEN_ONLY_TOOLS gate matches on (avery/tools.ts
 * PARKIFY_ADMIN_SCREEN). Kept as its own constant rather than importing
 * App.tsx's SCREEN_PATHS map to avoid the same circular-import problem this
 * whole file exists to route around (see the top-of-file comment) — must
 * stay in sync with SCREEN_PATHS.parkify_admin in App.tsx.
 */
export const PARKIFY_ADMIN_PATH = '/admin/parkify'

/**
 * Is `screen` hidden from this user because they're client-scoped?
 *
 * Pass the whole user, not `user.clientId` — `clientId == null` means Parkify
 * staff, so a missing user would be indistinguishable from staff and fail OPEN.
 * No user (auth still resolving) is treated as client-scoped instead.
 */
export function isStaffOnlyScreen(
  screen: ScreenId,
  user: { clientId?: string | null } | null | undefined,
): boolean {
  if (!PARKIFY_STAFF_ONLY.has(screen)) return false
  return !user || user.clientId != null
}

/**
 * Which per-tenant feature flag gates each screen — the SECOND axis, below the
 * staff-only set above and independent of it. A screen listed here renders only
 * when the caller's tenant hasn't switched its flag off on `Client.features`.
 *
 * Set these from Parkify Admin → Clients → (edit a client) → Feature flags. The
 * flag keys come from the registry in @parkify/api-types, so a typo here is a
 * type error rather than a silently ungated screen.
 *
 * Lives here rather than in App.tsx for the same reason PARKIFY_STAFF_ONLY does:
 * it needs to be reachable (and testable) without pulling in the whole app.
 *
 * `home` has no flag on purpose — it's the fallback a feature-blocked deep link
 * bounces to, so it must always stay reachable. Integrations / Parkify Admin /
 * Client Admin are role-gated rather than per-tenant, so they're absent too.
 * `tenants` deliberately shares the `validations` flag — it has no flag of its
 * own, so turning Validations off takes Tenants with it.
 */
export const SCREEN_FEATURE_FLAG: Partial<Record<ScreenId, FeatureFlagKey>> = {
  transactions: 'transactions',
  disputes:     'disputes',
  zones:        'zones',
  qr:           'qr',
  permits:      'permits',
  validations:  'validations',
  tenants:      'validations',
  enforcement:  'enforcement',
  accounting:   'accounting',
  analytics:    'analytics',
  customers:    'customers',
  subscriptions: 'subscribers',
  avery:        'avery',
}

/**
 * The role permission each screen requires — the FIRST axis. Moved here from
 * App.tsx so Home's shortcuts can ask the same question the router asks
 * (see `canOpenScreen` below) without a circular import.
 */
export const SCREEN_PERMS: Record<ScreenId, Permission> = {
  home:           'view.home',
  avery:          'view.avery',
  support:        'view.support',
  intake_submissions: 'view.intake_submissions',
  transactions:   'view.transactions',
  zones:          'view.zones',
  qr:             'view.qr',
  permits:        'view.permits',
  validations:    'view.validations',
  tenants:        'view.validations',
  accounting:     'view.accounting',
  analytics:      'view.analytics',
  integrations:   'view.integrations',
  parkify_admin:  'view.parkify_admin',
  client_admin:   'view.client_admin',
  customers:      'view.customers',
  subscriptions:  'view.subscribers',
  enforcement:    'view.enforcement',
  disputes:       'view.disputes',
  kpi_revenue:    'view.parkify_admin',
  // Base perm; the 2-person email allowlist (ALLOWLIST_ONLY below) narrows it
  // further so an owner/admin who isn't on the list still can't see it.
  kill_switches:  'view.parkify_admin',
  // Gated directly on the money permission rather than a view.* one — this
  // screen IS refund review, not a general view of something refund-adjacent,
  // and the only actions on it (approve/deny/mark-processed) already require
  // refund.session server-side (support/refund-request-queue.ts).
  refund_requests: 'refund.session',
}

/**
 * Screens restricted to a hardcoded named-email allowlist, stricter than any
 * role (see auth/killSwitchAdmins.ts). Layered on top of the perm + staff
 * gates. The server re-enforces the allowlist on every write (403) — this only
 * hides the nav item + bounces the route. Kill Switches flip Avery's autonomy
 * master switches, so per owner decision only two named people may reach them.
 */
export const ALLOWLIST_ONLY = new Set<ScreenId>(['kill_switches'])

export interface ScreenAccessCtx {
  hasPerm: (perm: Permission) => boolean
  hasFeature: (flag: FeatureFlagKey) => boolean
  user: { clientId?: string | null; email?: string | null; role?: string } | null | undefined
}

/**
 * Can this user open `screen`? Role permission AND tenant feature flag AND the
 * staff-only and named-allowlist gates — the whole rule the router enforces.
 *
 * App.tsx's sidebar and direct-URL guard use it, and so must every shortcut
 * that navigates to a screen (Home's quick actions, attention items, welcome
 * links, search results). A shortcut checking a hand-picked subset of these
 * axes offers a door the router then refuses — Home's "Issue a validation"
 * did exactly that for a user without view.validations (found in #3962).
 * A shortcut that needs MORE than this (an edit perm for an "Add …" action)
 * adds its own check on top; it never replaces this one.
 */
export function canOpenScreen(screen: ScreenId, { hasPerm, hasFeature, user }: ScreenAccessCtx): boolean {
  if (!hasPerm(SCREEN_PERMS[screen])) return false
  // Parkify-internal screens never show for a client-scoped tenant, even at
  // owner role — the role-perm check above is tenant-blind (O9/D3).
  if (isStaffOnlyScreen(screen, user)) return false
  if (screen === 'intake_submissions' && !['owner', 'admin', 'support', 'intake_viewer'].includes(user?.role ?? '')) return false
  if (ALLOWLIST_ONLY.has(screen) && !isKillSwitchAdmin(user?.email)) return false
  const flag = SCREEN_FEATURE_FLAG[screen]
  return !flag || hasFeature(flag)
}
